import Vue from "vue";
import moment from "moment";
import { prepareFormQuestions } from "@/services/forms";
import {
  GapState,
  GapFilter,
  GapPatientFilter,
  GapLogEntry,
  GapRecord,
  ProviderFilter,
  GapMeasureFilter,
  GapDateFilter,
  User,
  GapReportFilter,
  GapTransportationCoordinationReportFilter,
  GapCoordinationSummaryReportFilter,
  GapStatusSummaryReportFilter,
  GapMemberCallReportFilter
} from "./types";

const getDefaultFilter = (): GapFilter => {
  return {
    entityId: "",
    campaignId: "",
    id: "",
    patient: {
      id: "",
      firstName: "",
      lastName: "",
      insurerMemberId: "",
      EMR_ID: "",
      birthdateFrom: null,
      birthdateTo: null,
      city: "",
      gender: "",
      phone: "",
      insurerList: [],
      ipaList: []
    },
    insurer: {
      id: "",
      memberId: "",
    },
    provider: {
      id: "",
      npi: "",
      list: [],
      cache: {
        name: "",
        campaignId: "",
        measureTypeId: "",
        list: [],
      },
    },
    measure: {
      typeId: "",
      list: [],
    },
    referenceDate: {
      from: moment().startOf('year').toDate(),
      to: moment().endOf('year').toDate(),
    },
    priorityList: [],
    statusList: [],
    subStatusList: [],
    reasonList: [],
    ownerUserList: [],
    logTypeList:[],
    created: {
      from: null,
      to: null,
    },
    updated: {
      from: null,
      to: null,
    },
    transportation:null,
    comment:null,
    orderBy: "",
    pageNumber: 1,
    rowsPerPage: 25,
    report: {
      insurerList: [],
      ipaList: [],
      priorityList: [],
      statusList: [],
      apptStatusList: [],
      subStatusList: [],
      admissionDischargeList: [],
      providerList: [],
      ownerUserList:[],
      referenceDate: {
        from: null,
        to: null,
      },
       createdDate: {
        from: null,
        to: null,
      },
      updated: {
        from: null,
        to: null,
      },
      campaignId: null,
      apptDate: {
        from: null,
        to: null,
      },
      ipaNumber: ""
    }
  };
};

const getDefaultState = () => {
  const state: GapState = {
    campaigns: [],
    insurers: [],
    providers: [],
    measures: [],
    measureTypes: [],
    measureTypeTemplates: [],
    priority: [],
    status: [],
    subStatus: [],
    reasons: [],
    logTypes: [],
    users: [],
    ipas: [],
    reportLinks: [],
    reportLink: "",
    filter: getDefaultFilter(),
  };
  return state;
};

const filterOn = (filter: any) => {
  if (typeof filter === "object") {
    for (const field in filter) {
      const value = filter[field];
      if (field !== "cache") {
        if (Array.isArray(value)) {
          if (value.length) return true;
        } else if (value) {
          return true;
        }
      }
    }
  } else {
    return filter ? true : false;
  }
  return false;
};

export default {
  namespaced: true,
  state: getDefaultState(),
  mutations: {
    SET_CAMPAIGNS(state: GapState, campaigns: Array<any>) {
      state.campaigns = campaigns;
    },
    SET_INSURERS(state: GapState, insurers: Array<any>) {
      state.insurers = insurers;
    },
    SET_IPAS(state: GapState, ipas: Array<any>) {
      state.ipas = ipas;
    },
    SET_REPORT_LINKS(state: GapState, links: Array<any>) {
      state.reportLinks = links;
    },
    SET_PROVIDERS(state: GapState, providers: Array<any>) {
      state.providers = providers;
    },
    SET_MEASURES(state: GapState, measures: Array<any>) {
      state.measures = measures;
    },
    SET_MEASURE_TYPES(state: GapState, measureTypes: Array<any>) {
      state.measureTypes = measureTypes;
    },
    SET_MEASURE_TYPE_TEMPLATES(
      state: GapState,
      measureTypeTemplates: Array<any>
    ) {
      state.measureTypeTemplates = measureTypeTemplates;
    },
    SET_PRIORITY(state: GapState, priority: Array<any>) {
      state.priority = priority;
    },
    SET_STATUS(state: GapState, status: Array<any>) {
      state.status = status;
    },
    SET_SUB_STATUS(state: GapState, subStatus: Array<any>) {
      state.subStatus = subStatus;
    },
    SET_REASONS(state: GapState, reasons: Array<any>) {
      state.reasons = reasons;
    },
    SET_LOG_TYPES(state: GapState, logTypes: Array<any>) {
      state.logTypes = logTypes;
    },
    SET_USERS(state: GapState, users: Array<any>) {
      state.users = users;
    },
    SET_FILTER(state: GapState, filter: GapFilter) {
      state.filter = filter;
    },
    SET_ENTITY_FILTER(state: GapState, id: string) {
      state.filter.entityId = id;
    },
    SET_CAMPAIGN_FILTER(state: GapState, id: string) {
      state.filter.campaignId = id;
    },
    SET_ID_FILTER(state: GapState, id: string) {
      state.filter.id = id;
    },
    SET_PATIENT_FILTER(state: GapState, filter: GapPatientFilter) {
      state.filter.patient = filter;
    },
    SET_PROVIDER_FILTER(state: GapState, filter: ProviderFilter) {
      state.filter.provider.cache = filter;
      state.filter.provider.list = filter.list
        .filter((provider) => {
          return provider.checked;
        })
        .map((provider) => provider.id);
    },
    SET_MEASURE_FILTER(state: GapState, filter: GapMeasureFilter) {
      state.filter.measure = filter;
    },
    SET_REFDATE_FILTER(state: GapState, filter: GapDateFilter) {
      state.filter.referenceDate = filter;
    },
    SET_PRIORITY_FILTER(state: GapState, filter: string[]) {
      state.filter.priorityList = filter;
    },
    SET_STATUS_FILTER(state: GapState, filter: string[]) {
      state.filter.statusList = filter;
    },
    SET_SUB_STATUS_FILTER(state: GapState, filter: string[]) {
      state.filter.subStatusList = filter;
    },
    SET_LOG_TYPE_FILTER(state: GapState, filter: string[]) {
      state.filter.logTypeList = filter;
    },
    SET_OWNER_USER_FILTER(state: GapState, filter: number[]) {
      state.filter.ownerUserList = filter;
    },
    SET_CREATED_FILTER(state: GapState, filter: GapDateFilter) {
      state.filter.created = filter;
    },
    SET_UPDATED_FILTER(state: GapState, filter: GapDateFilter) {
      state.filter.updated = filter;
    },
    SET_INSURERS_FILTER(state: GapState, filter: number[]) {
      state.filter.patient.insurerList = filter;
    },
    SET_REASONS_FILTER(state: GapState, filter: number[]) {
      state.filter.reasonList = filter;
    },
    SET_ORDER_BY_FILTER(state: GapState, field: string) {
      state.filter.orderBy = field;
      state.filter.pageNumber = 1;
    },
    SET_TRANSPORTATION_FILTER(state: GapState, transportation: boolean) {
      state.filter.transportation = transportation;
    },
    SET_PAGE_NUMBER(state: GapState, number: number) {
      state.filter.pageNumber = number;
    },
    SET_ROWS_PER_PAGE(state: GapState, number: number) {
      state.filter.rowsPerPage = number;
    },
    RESET_FILTER(state: GapState, entityId: string) {
      state.filter = getDefaultFilter();
      if (entityId) {
        state.filter.entityId = entityId;
      }
    },
    RESET_STATE(state: GapState) {
      Object.assign(state, getDefaultState());
    },
    //REPORT FILTER
    SET_REPORT_INSURERS_FILTER(state: GapState, filter: number[]) {
      state.filter.report.insurerList = filter;
    },
    SET_REPORT_STATUS_FILTER(state: GapState, filter: string[]) {
      state.filter.report.statusList = filter;
    },
    SET_REPORT_APPT_STATUS_FILTER(state: GapState, filter: number[]) {
      state.filter.report.apptStatusList = filter;
    },
    SET_REPORT_SUB_STATUS_FILTER(state: GapState, filter: string[]) {
      state.filter.report.subStatusList = filter;
    },
    SET_REPORT_ADMISSION_DISCHARGE_FILTER(state: GapState, filter: string[]) {
      state.filter.report.admissionDischargeList = filter;
    },
    SET_REPORT_REFERENCE_DATE_FILTER(state: GapState, filter: GapDateFilter) {
      state.filter.report.referenceDate = filter;
    },
    SET_REPORT_PROVIDERS_FILTER(state: GapState, filter: number[]) {
      state.filter.report.providerList = filter;
    },
    SET_REPORT_APPT_DATE_FILTER(state: GapState, filter: GapDateFilter) {
      state.filter.report.apptDate = filter;
    },
  },
  actions: {
    async getList({ rootState }) {
      // SET FORM DATA
      const { filter } = rootState.gaps as GapState;
      const params = new FormData();
      if (filter.provider.list && filter.provider.list.length) {
        params.append("providerList", filter.provider.list.join());
      }
      if (filter.priorityList && filter.priorityList.length) {
        params.append(
          "priorityList",
          filter.priorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join(",")
        );
      }
      if (filter.measure.list && filter.measure.list.length) {
        params.append("measureList", filter.measure.list.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      if (filter.subStatusList && filter.subStatusList.length) {
        params.append(
          "subStatusList",
          filter.subStatusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      if (filter.reasonList && filter.reasonList.length) {
        params.append("reasonList", filter.reasonList.join(","));
      }
      if (filter.ownerUserList && filter.ownerUserList.length) {
        params.append("ownerUserList", filter.ownerUserList.join());
      }
      if (filter.patient.insurerList && filter.patient.insurerList.length) {
        params.append("patientInsurerList", filter.patient.insurerList.join(","));
      }
      if (filter.patient.ipaList && filter.patient.ipaList.length) {
        params.append("patientIPAList", filter.patient.ipaList.map((IPA) => {
          return "'" + IPA + "'";
        }).join(","));
      }
      params.append("entityId", filter.entityId);
      params.append("campaignId", filter.campaignId);
      params.append("id", filter.id);
      params.append("insurerId", filter.insurer.id);
      params.append("insurerMemberId", filter.insurer.memberId);
      params.append("providerId", filter.provider.id);
      params.append("providerNPI", filter.provider.npi);
      params.append("measureTypeId", filter.measure.typeId);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdateFrom).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdateTo).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      if(filter.transportation)params.append("transportation", filter.transportation.toString());
      if(filter.comment)params.append("comment", filter.comment.toString());
      params.append("orderBy", filter.orderBy);
      params.append("pageNumber", String(filter.pageNumber));
      params.append("rowsPerPage", String(filter.rowsPerPage));
      // GET GAP LIST ASYNC
      const { data } = await Vue.axios.post("gaps/list", params);
      //
      return data;
    },
    async getListCSV({ rootState }) {
      // SET FORM DATA
      const { filter } = rootState.gaps as GapState;
      const params = new FormData();
      if (filter.entityId) {
        params.append("entityId", filter.entityId);
      }
      if (filter.provider.list && filter.provider.list.length) {
        params.append("providerList", filter.provider.list.join());
      }
      if (filter.priorityList && filter.priorityList.length) {
        params.append(
          "priorityList",
          filter.priorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join(",")
        );
      }
      if (filter.measure.list && filter.measure.list.length) {
        params.append("measureList", filter.measure.list.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      if (filter.subStatusList && filter.subStatusList.length) {
        params.append(
          "subStatusList",
          filter.subStatusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      params.append("id", filter.id);
      params.append("campaignId", filter.campaignId);
      params.append("insurerId", filter.insurer.id);
      params.append("insurerMemberId", filter.insurer.memberId);
      params.append("providerId", filter.provider.id);
      params.append("providerNPI", filter.provider.npi);
      params.append("measureTypeId", filter.measure.typeId);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdateFrom).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdateTo).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      params.append("orderBy", filter.orderBy);
      params.append("pageNumber", String(filter.pageNumber));
      params.append("rowsPerPage", String(filter.rowsPerPage));
      // GET GAP LIST ASYNC
      const { data } = await Vue.axios.post("gaps/list/csv", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getListLogExcel({ rootState }) {
      // SET FORM DATA
      const { filter } = rootState.gaps as GapState;
      const params = new FormData();
      if (filter.entityId) {
        params.append("entityId", filter.entityId);
      }
      if (filter.provider.list && filter.provider.list.length) {
        params.append("providerList", filter.provider.list.join());
      }
      if (filter.priorityList && filter.priorityList.length) {
        params.append(
          "priorityList",
          filter.priorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join(",")
        );
      }
      if (filter.measure.list && filter.measure.list.length) {
        params.append("measureList", filter.measure.list.join());
      }
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      if (filter.subStatusList && filter.subStatusList.length) {
        params.append(
          "subStatusList",
          filter.subStatusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      params.append("id", filter.id);
      params.append("campaignId", filter.campaignId);
      params.append("insurerId", filter.insurer.id);
      params.append("insurerMemberId", filter.insurer.memberId);
      params.append("providerId", filter.provider.id);
      params.append("providerNPI", filter.provider.npi);
      params.append("measureTypeId", filter.measure.typeId);
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append("patientGender", filter.patient.gender);
      params.append("patientCity", filter.patient.city);
      params.append(
        "patientBirthdateFrom",
        moment(filter.patient.birthdateFrom).format("YYYY-MM-DD")
      );
      params.append(
        "patientBirthdateTo",
        moment(filter.patient.birthdateTo).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      params.append("orderBy", filter.orderBy);
      params.append("pageNumber", String(filter.pageNumber));
      params.append("rowsPerPage", String(filter.rowsPerPage));
      // GET GAP LIST ASYNC
      const { data } = await Vue.axios.post("gaps/list/log/excel", params, {
        responseType: "blob",
      });
      //
      return data;
    },
    async getFilters({ rootState }) {
      // GET USER FILTERS ASYNC
      const user: User = rootState.auth.user;
      const ownerFilter = getDefaultFilter();
      ownerFilter.ownerUserList = [user.id];
      const filters = [
        {
          id: -1,
          name: "No Filter",
          json: JSON.stringify(getDefaultFilter()),
        },
        {
          id: 0,
          name: "My Gaps",
          json: JSON.stringify(ownerFilter),
        },
      ];
      const { data } = await Vue.axios.post("gaps/user/filter/list");
      filters.push(...data);
      //
      return filters;
    },
    async getGapProviders(
      { rootState },
      { campaignId, measureTypeId, gapList }
    ) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("campaignId", campaignId);
      params.append("measureTypeId", measureTypeId);
      params.append("gapList", gapList);
      // GET GAP LIST ASYNC
      const { data } = await Vue.axios.post("gaps/list/providers", params);
      //
      return data;
    },
    async getFiles({ commit }, gapId: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", String(gapId));
      // GET GAP FILES ASYNC
      const { data } = await Vue.axios.post("gaps/file/list", params);
      //
      return data;
    },
    async getFileUrl({ commit }, { gapId, storageId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("storageId", storageId);
      // GET GAP FILE URL
      const { data } = await Vue.axios.post("gaps/file/url", params);
      //
      return data;
    },
    async getFileDownloadUrl({ commit }, { gapId, storageId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("storageId", storageId);
      // GET GAP FILE URL
      const { data } = await Vue.axios.post("gaps/file/url/download", params);
      //
      return data;
    },
      async getPatientFileDownloadUrl({ commit }, { gapId, storageId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("storageId", storageId);
      // GET GAP FILE URL
      const { data } = await Vue.axios.post("gaps/patient/file/url/download", params);
      //
      return data;
    },
    async getLabTests({ commit }, gapId: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", String(gapId));
      // GET GAP LAB TESTS ASYNC
      const { data } = await Vue.axios.post("gaps/lab/test/list", params);
      //
      return data;
    },
    async getCount({ rootState }, filter: GapFilter) {
      // SET FORM DATA
      const params = new FormData();
      if (filter.entityId) {
        params.append("entityId", filter.entityId);
      }
      //
      params.append("campaignId", String(filter.campaignId));
      params.append("insurerId", String(filter.insurer.id));
      params.append("insurerMemberId", filter.insurer.memberId);
      params.append("providerId", String(filter.provider.id));
      params.append("measureTypeId", filter.measure.typeId);
      // params.append("measureId", String(filter.measure.id));
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      // GET STATUS COUNT ASYNC
      const { data } = await Vue.axios.post("gaps/count", params);
      //
      return data;
    },
    async getPriorityCount({ rootState }, filter: GapFilter) {
      // SET FORM DATA
      const params = new FormData();
      if (filter.entityId) {
        params.append("entityId", filter.entityId);
      }
      params.append("campaignId", String(filter.campaignId));
      params.append("insurerId", String(filter.insurer.id));
      params.append("insurerMemberId", filter.insurer.memberId);
      params.append("providerId", String(filter.provider.id));
      params.append("measureTypeId", filter.measure.typeId);
      // params.append("measureId", String(filter.measure.id));
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      // GET STATUS COUNT ASYNC
      const { data } = await Vue.axios.post("gaps/count/priority", params);
      //
      return data;
    },
    async getStatusCount({ rootState }, filter: GapFilter) {
      // SET FORM DATA
      const params = new FormData();
      if (filter.entityId) {
        params.append("entityId", filter.entityId);
      }
      params.append("campaignId", String(filter.campaignId));
      params.append("insurerId", String(filter.insurer.id));
      params.append("insurerMemberId", filter.insurer.memberId);
      params.append("providerId", String(filter.provider.id));
      params.append("measureTypeId", filter.measure.typeId);
      // params.append("measureId", String(filter.measure.id));
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      // GET STATUS COUNT ASYNC
      const { data } = await Vue.axios.post("gaps/count/status", params);
      //
      return data;
    },
    async getSubStatusCount({ rootState }, filter: GapFilter) {
      // SET FORM DATA
      const params = new FormData();
      if (filter.entityId) {
        params.append("entityId", filter.entityId);
      }
      params.append("campaignId", String(filter.campaignId));
      params.append("insurerId", String(filter.insurer.id));
      params.append("insurerMemberId", filter.insurer.memberId);
      params.append("providerId", String(filter.provider.id));
      params.append("measureTypeId", filter.measure.typeId);
      // params.append("measureId", String(filter.measure.id));
      params.append("patientFirstName", filter.patient.firstName);
      params.append("patientLastName", filter.patient.lastName);
      params.append("patientInsurerMemberId", filter.patient.insurerMemberId);
      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateFrom",
        moment(filter.created.from).format("YYYY-MM-DD")
      );
      params.append(
        "recordDateTo",
        moment(filter.created.to).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateFrom",
        moment(filter.updated.from).format("YYYY-MM-DD")
      );
      params.append(
        "lastUpdateTo",
        moment(filter.updated.to).format("YYYY-MM-DD")
      );
      // GET SUB-STATUS COUNT ASYNC
      const { data } = await Vue.axios.post("gaps/count/substatus", params);
      //
      return data;
    },
    async getRecord({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", String(id));
      // GET GAP ASYNC
      const { data } = await Vue.axios.post("gaps/record", params);
      //
      return data;
    },
    async getLogById({ commit }, {id,typeList}) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", String(id));
      params.append("typeList", typeList.join(","));
      // SET GAP LOG ASYNC
      const { data } = await Vue.axios.post("gaps/log", params);
      //
      return data;
    },
    async getLogTypes({ commit, state }) {
      if (state.logTypes.length) return;
      // GET LOG TYPES ASYNC
      const { data } = await Vue.axios.post("gaps/log/type/list");
      //
      commit("SET_LOG_TYPES", data);
      return data;
    },
    async getMeasures({ commit, state }) {
      if (state.measures.length) return;
      // GET MEASURES ASYNC
      const { data } = await Vue.axios.post("gaps/measure/list");
      //
      commit("SET_MEASURES", data);
    },
    async getMeasureTypes({ commit, state }) {
      if (state.measureTypes.length) return;
      // GET MEASURE TYPES ASYNC
      const { data } = await Vue.axios.post("gaps/measure/type/list");
      //
      commit("SET_MEASURE_TYPES", data);
    },
    async getMeasureTypeTemplates({ rootState }, measureTypeId: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("measureTypeId", measureTypeId);
      // GET TEMPLATES ASYNC
      const { data } = await Vue.axios.post(
        "gaps/measure/type/template/list",
        params
      );
      //
      return data;
    },
    async getProviderTemplates({ rootState }, measureTypeId: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("measureTypeId", measureTypeId);
      // GET TEMPLATES ASYNC
      const { data } = await Vue.axios.post(
        "gaps/provider/template/list",
        params
      );
      //
      return data;
    },
    async getPriority({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET PRIORITY LIST ASYNC
      const { data } = await Vue.axios.post(
        "gaps/priority/entity/list",
        params
      );
      //
      commit("SET_PRIORITY", data);
    },
    async getStatus({ commit, state }) {
      if (state.status.length) return;
      // GET STATUS LIST ASYNC
      const { data } = await Vue.axios.post("gaps/status/list");
      //
      commit("SET_STATUS", data);
    },
    async getSubStatus({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET SUB-STATUS LIST ASYNC
      const { data } = await Vue.axios.post(
        "gaps/substatus/entity/list",
        params
      );
      //
      commit("SET_SUB_STATUS", data);
    },
    async getReasons({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      //
      const { data } = await Vue.axios.post("gaps/reason/list", params);
      commit("SET_REASONS", data);
    },
    async getSubReasons({ commit, rootState }, reasonId: string) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("reasonId", String(reasonId));
      // GET SUB-REASONS ASYNC
      const { data } = await Vue.axios.post(
        "gaps/subreason/list",
        params
      );
      //
      return data;
    },
    async getCampaigns({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }
      //
      const { data } = await Vue.axios.post("gaps/campaign/list", params);
      commit("SET_CAMPAIGNS", data);
    },
    async getInsurers({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }
      //
      const { data } = await Vue.axios.post("gaps/insurer/list", params);
      commit("SET_INSURERS", data);
    },
    async getIPA({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }
      //
      const { data } = await Vue.axios.post("gaps/ipa/list", params);
      commit("SET_IPAS", data);
    },
    async getProviders({ commit, rootState }, filter: ProviderFilter) {
      // SET FORM PARAMS
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) params.append("entityId", entityId);

      if (filter) {
        // if (entityId) params.append("entityId", entityId);
        if (filter.campaignId || filter.measureTypeId) {
          params.append("enrolled", "true");
        }
        if (filter.name) params.append("name", filter.name);
        if (filter.campaignId)
          params.append("campaignId", String(filter.campaignId));
        if (filter.measureTypeId)
          params.append("measureTypeId", filter.measureTypeId);
      }
      //
      const { data } = await Vue.axios.post("gaps/provider/list", params);
      return data;
    },
    async getReportLinks({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }
      //
      const { data } = await Vue.axios.post("gaps/list/report/links", params);
      commit("SET_REPORT_LINKS", data);
    },
    async getPatients({ rootState }, filter: GapPatientFilter) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("patientId", filter.id);
      params.append("firstName", filter.firstName);
      params.append("lastName", filter.lastName);
      params.append("phone", filter.phone);
      params.append("insurerMemberId", filter.insurerMemberId);
      params.append("EMR_ID", filter.EMR_ID);
      // GET GAP PATIENTS ASYNC
      const { data } = await Vue.axios.post("gaps/patient/list", params);
      //
      return data;
    },
    async getSummary({ rootState }, campaignId) {
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }
      params.append("campaignId", campaignId.campaignId);
      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/summary", params);
      //
      return data;
    },
    async getSummaryExel({ rootState }, { campaignId, detailed }) {
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }
      if (detailed) {
        params.append("detailed", detailed);
      }
      params.append("campaignId", campaignId.campaignId);
      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/summary/excel", params,
        {
          responseType: "blob",
        });
      //
      return data;
    },
    async getLabTestTypes({ commit, rootState }) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET LAB TEST TYPES ASYNC
      const { data } = await Vue.axios.post("gaps/lab/test/type/list", params);
      //
      return data;
    },
    async getLinks({ commit }, id: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id.toString());
      // GET LINKS ASYNC
      const { data } = await Vue.axios.post("gaps/link/list", params);
      //
      return data;
    },
    async getForms({ rootState }, measureTypeId: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      params.append("measureTypeId", measureTypeId);
      // GET FORMS ASYNC
      const { data } = await Vue.axios.post("gaps/form/list", params);
      //
      return data;
    },
    async getGapForms({ rootState }, gapId: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      // GET FORMS ASYNC
      const { data } = await Vue.axios.post("gaps/form/list/gap", params);
      //
      return data;
    },
    async getFormQuestions({ commit }, id: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      // GET FORM QUESTIONS ASYNC
      const { data } = await Vue.axios.post("gaps/form/question/list", params);
      //
      return prepareFormQuestions(data);
    },
    async getUsers({ rootState, commit }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("entityId", rootState.auth.entityId);
      // GET USERS ASYNC
      const { data } = await Vue.axios.post("entity/user/list", params);
      //
      commit("SET_USERS", data);
    },
    async getPatientGapFiles({ commit }, { dob, phone,publicId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("birthdate", dob ? dob : "");
      params.append("phone", phone);
      params.append("publicId", publicId);
      // SAVE REASON ASYNC
      const { data } = await Vue.axios.post("gaps/patient/files", params);
      //
      return data;
    },
    async save({ rootState }, gap: GapRecord) {
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (gap.id) params.append("id", String(gap.id));
      params.append("entityId", entityId);
      params.append("campaignId", gap.campaignId);
      params.append("providerId", gap.providerId);
      params.append("insurerId", gap.insurerId);
      params.append("insurerMemberId", gap.insurerMemberId);
      params.append("patientId", gap.patientId);
      params.append("patientPhoneType", gap.patientPhoneType);
      params.append("patientPhone", gap.patientPhone);
      params.append("description", gap.description);
      params.append("measureId", gap.measureId);
      params.append(
        "referenceDate",
        moment(gap.referenceDate).format("YYYY-MM-DD")
      );
      params.append(
        "admissionDate",
        moment(gap.admissionDate).format("YYYY-MM-DD")
      );
      params.append("admissionDiagnosis", gap.admissionDiagnosis);
      params.append("admissionProviderName", gap.admissionProviderName);
      params.append("testDate", moment(gap.testDate).format("YYYY-MM-DD"));
      params.append("testResultValue", gap.testResultValue);
      params.append("dischargeDiagnosis", gap.dischargeDiagnosis);
      params.append("facilityName", gap.facilityName);
      params.append("pharmacyName", gap.pharmacyName);
      params.append("medicationName", gap.medicationName);
      params.append("comment", gap.comment);
      params.append("lang", gap.lang);
      params.append("priorityId", gap.priorityId);
      params.append("statusId", gap.statusId);
      params.append("subStatusId", gap.subStatusId);
      params.append("closeReasonId", gap.closeReasonId);
      params.append("closeSubReasonId", gap.closeSubReasonId);
      params.append("closeDate", moment(gap.closeDate).format("YYYY-MM-DD"));
      params.append("refGapId", gap.refGapId);
      params.append("ownerUserId", gap.ownerUserId);
      if (gap.transportation) params.append("transportation", String(gap.transportation));
      // SAVE GAP ASYNC
      const { data } = await Vue.axios.post("gaps/save", params);
      //
      return data;
    },
    async saveLog({ commit }, entry: GapLogEntry) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", String(entry.gapId));
      params.append("typeId", entry.typeId);
      params.append("statusId", entry.statusId ? String(entry.statusId) : "");
      params.append(
        "subStatusId",
        entry.subStatusId ? String(entry.subStatusId) : ""
      );
      params.append("reasonId", String(entry.reasonId));
      params.append("comment", entry.comment ? entry.comment : "");
      // SAVE GAP LOG ASYNC
      const { data } = await Vue.axios.post("gaps/log/save", params);
      //
      return data;
    },
    async saveSystemLog({ commit }, entry: GapLogEntry) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", String(entry.gapId));
      params.append("typeId", entry.typeId);
      params.append("statusId", entry.statusId ? String(entry.statusId) : "");
      params.append(
        "subStatusId",
        entry.subStatusId ? String(entry.subStatusId) : ""
      );
      params.append("comment", entry.comment ? entry.comment : "");
      // SAVE GAP LOG ASYNC
      const { data } = await Vue.axios.post("gaps/log/save/system", params);
      //
      return data;
    },
    async savePriority({ commit }, { gapId, gapList, priorityId, comment }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId ? gapId : "");
      params.append("gapList", gapList ? gapList : "");
      params.append("priorityId", priorityId);
      params.append("comment", comment);
      // SAVE PRIORITY ASYNC
      const { data } = await Vue.axios.post("gaps/priority/save", params);
      //
      return data;
    },
    async saveStatus({ commit }, { gapId, gapList, statusId, comment }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId ? gapId : "");
      params.append("gapList", gapList ? gapList : "");
      params.append("statusId", statusId);
      params.append("comment", comment);
      // SAVE STATUS ASYNC
      const { data } = await Vue.axios.post("gaps/status/save", params);
      //
      return data;
    },
    async saveSubStatus({ commit }, { gapId, gapList, subStatusId, comment }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId ? gapId : "");
      params.append("gapList", gapList ? gapList : "");
      params.append("subStatusId", subStatusId);
      params.append("comment", comment);
      // SAVE SUB-STATUS ASYNC
      const { data } = await Vue.axios.post("gaps/substatus/save", params);
      //
      return data;
    },

    async saveReason({ commit }, { gapId, reasonId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId ? gapId : "");
      params.append("reasonId", reasonId);
      // SAVE REASON ASYNC
      const { data } = await Vue.axios.post("gaps/reason/save", params);
      //
      return data;
    },
     async saveProvider({ commit }, { gapId, providerId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId ? gapId : "");
      params.append("providerId", providerId);
      // SAVE REASON ASYNC
      const { data } = await Vue.axios.post("gaps/provider/save", params);
      //
      return data;
    },
    async saveFile({ rootState }, { gapId, fileId }) {
      // SAVE FILE ASYNC
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("fileId", fileId);
      const { data } = await Vue.axios.post("gaps/file/save", params);
      //
      return data;
    },
    async saveFileDesc({ commit }, { gapId, fileId, description }) {
      // SAVE FILE DESC TO DB
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("fileId", fileId);
      params.append("description", description);
      const { data } = await Vue.axios.post("gaps/file/desc", params);
      //
      return data;
    },
    async saveLabTest({ rootState }, { gapId, test }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("typeId", test.typeId);
      params.append("name", test.name);
      params.append("date", moment(test.date).format("YYYY-MM-DD"));
      if (test.id) params.append("testId", test.id);
      if (test.testComment) params.append("testComment", test.testComment);
      if (test.resultValue) params.append("resultValue", test.resultValue);
      if (test.resultComment)
        params.append("resultComment", test.resultComment);
      if (test.fileId) params.append("fileId", test.fileId);
      // SAVE LAB TEST ASYNC
      const { data } = await Vue.axios.post("gaps/lab/test/save", params);
      //
      return data;
    },
    async saveFilter({ rootState }, name: string) {
      // SET FORM DATA
      const params = new FormData();
      params.append("name", name);
      params.append("json", JSON.stringify(rootState.gaps.filter));
      // SAVE FILTER ASYNC
      const { data } = await Vue.axios.post("gaps/user/filter/save", params);
      //
      return data;
    },
    async saveLink({ commit }, { gapId, id, url, description }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("id", id);
      params.append("url", url);
      params.append("description", description);
      // SAVE LINK ASYNC
      const { data } = await Vue.axios.post("gaps/link/save", params);
      //
      return data;
    },
    async saveForm({ rootState }, { gapFormId, gapId, id }) {
      // SAVE FORM ASYNC
      const params = new FormData();
      params.append("gapFormId", gapFormId);
      params.append("gapId", gapId);
      params.append("id", id);
      const { data } = await Vue.axios.post("gaps/form/save", params);
      //
      return data;
    },
    async saveFormAnswers({ commit }, { id, answers }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("id", id);
      params.append("list", JSON.stringify(answers));
      // SAVE ANSWERS ASYNC
      const { data } = await Vue.axios.post(
        "gaps/form/answer/save/list",
        params
      );
      //
      return data;
    },
    async archiveFile({ commit }, { gapId, fileId }) {
      // SAVE FILE DESC TO DB
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("fileId", fileId);
      const { data } = await Vue.axios.post("gaps/file/archive", params);
      //
      return data;
    },
    async archiveLabTest({ commit }, { gapId, testId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("testId", testId);
      // ARCHIVE LAB TEST ASYNC
      const { data } = await Vue.axios.post("gaps/lab/test/archive", params);
      //
      return data;
    },
    async archiveFilter({ commit }, filterId: number) {
      // SET FORM PARAMS
      const params = new FormData();
      params.append("filterId", String(filterId));
      // ARCHIVE FILTER ASYNC
      const { data } = await Vue.axios.post("gaps/user/filter/archive", params);
      //
      return data;
    },
    async archiveLink({ rootState }, { gapId, id }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("gapId", gapId);
      params.append("id", id);
      // ARCHIVE LINK ASYNC
      const { data } = await Vue.axios.post("gaps/link/archive", params);
      //
      return data;
    },
    async archiveForm({ rootState }, id: string) {
      // SAVE FORM ASYNC
      const params = new FormData();
      params.append("id", id);
      const { data } = await Vue.axios.post("gaps/form/archive", params);
      //
      return data;
    },
    async markLogRead({ commit }, logId: number) {
      // SET FORM DATA
      const params = new FormData();
      params.append("logId", String(logId));
      // MARK LOG READ ASYNC
      const { data } = await Vue.axios.post("gaps/log/mark/read", params);
      //
      return data;
    },
    async getMembersReport({ rootState }, campaignId) {

      const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }

      if (filter.report.statusList && filter.report.statusList.length) {
        params.append(
          "statusList",
          filter.report.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      if (filter.report.subStatusList && filter.report.subStatusList.length) {
        params.append(
          "subStatusList",
          filter.report.subStatusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }

      if (filter.report.insurerList && filter.report.insurerList.length) {
        params.append("insurerList", filter.report.insurerList.join(","));
      }

      if (filter.report.admissionDischargeList && filter.report.admissionDischargeList.length) {
        params.append(
          "admissionDischargeList",
          filter.report.admissionDischargeList
            .map((admissionDischarge) => {
              return "'" + admissionDischarge + "'";
            })
            .join(",")
        );
      }

      if (filter.report.ownerUserList && filter.report.ownerUserList.length) {
        params.append("ownerUserList", filter.report.ownerUserList.join(","));
      }

      if(filter.report.referenceDate.from)
      params.append(
        "referenceDateFrom",
        moment(filter.report.referenceDate.from).format("YYYY-MM-DD")
      );

      if(filter.report.referenceDate.to)
      params.append(
        "referenceDateTo",
        moment(filter.report.referenceDate.to).format("YYYY-MM-DD")
      );

      if(filter.report.updated.from)
      params.append(
        "updatedFrom",
        moment(filter.report.updated.from).format("YYYY-MM-DD")
      );

      if(filter.report.updated.to)
      params.append(
        "updatedTo",
        moment(filter.report.updated.to).format("YYYY-MM-DD")
      );

      if(filter.report.createdDate.from)
      params.append(
        "createdFrom",
        moment(filter.report.createdDate.from).format("YYYY-MM-DD")
      );

      if(filter.report.createdDate.to)
      params.append(
        "createdTo",
        moment(filter.report.createdDate.to).format("YYYY-MM-DD")
      );

      if(filter.report.memberId){
       params.append("memberId", filter.report.memberId);
      }

      if(filter.report.member?.firstName){
       params.append("memberFirstName", filter.report.member?.firstName);
      }

      if(filter.report.member?.lastName){
       params.append("memberLastName", filter.report.member?.lastName);
      }

      if(filter.report.member?.gender){
       params.append("memberGender", filter.report.member?.gender);
      }

      if(filter.report.member?.birthdateFrom){
       params.append("memberBirthdateFrom",  moment(filter.report.member?.birthdateFrom).format("YYYY-MM-DD"));
      }

      if(filter.report.member?.birthdateTo){
       params.append("memberBirthdateTo", moment(filter.report.member?.birthdateTo).format("YYYY-MM-DD"));
      }
    
      params.append("campaignId", campaignId);
      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/members", params);
      //
      return data;
    },
    async getMemberCallReport({ rootState }, filter: GapMemberCallReportFilter) {

      //const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }


      if (filter.campaignId) {
        params.append("campaignId", filter.campaignId.toString());
      }

      if (filter.createdDate.from) {
        params.append(
          "dateFrom",
          moment(filter.createdDate.from).format("YYYY-MM-DD")
        );
      }
      if (filter.createdDate.to) {
        params.append(
          "dateTo",
          moment(filter.createdDate.to).format("YYYY-MM-DD")
        );
      }

      /*
      params.append(
        "dateFrom",
        moment(filter.createdDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "dateTo",
        moment(filter.createdDate.to).format("YYYY-MM-DD")
      );

      //end test
      params.append("campaignId", filter.campaignId?filter.campaignId);*/
      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/member/call", params);
      //
      return data;
    },
    async getApptStatusReport({ rootState }, campaignId) {

      const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }

      if (filter.report.providerList && filter.report.providerList.length) {
        params.append("providerList", filter.report.providerList.join(","));
      }

      if (filter.report.apptDate?.from) {
        params.append(
          "apptDateFrom",
          moment(filter.report.apptDate.from).format("YYYY-MM-DD")
        );
      }
      if (filter.report.apptDate?.to) {
        params.append(
          "apptDateTo",
          moment(filter.report.apptDate.to).format("YYYY-MM-DD")
        );
      }

      params.append("campaignId", campaignId);
      
      if(filter.report.apptStatusList)
      params.append("statusList", filter.report.apptStatusList.join());
      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/appt/status", params);
      //
      return data;
    },
    async getYearlyStatusReport({ rootState }, filter: GapReportFilter) {

      // const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }

      if (filter.ipaNumber) {
        params.append("IPANumber", filter.ipaNumber);
      }

      if (filter.campaignId) {
        params.append("campaignId", filter.campaignId.toString());
      }

      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/yearly/status", params);
      //
      return data;
    },
    async getStatusSummaryReport({ rootState }, filter: GapStatusSummaryReportFilter) {

      // const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }

      params.append("period", filter.period);

      if (filter.campaignId) {
        params.append("campaignId", filter.campaignId.toString());
      }

      if (filter.createdDate.from) {
        params.append(
          "createdDateFrom",
          moment(filter.createdDate.from).format("YYYY-MM-DD")
        );
      }
      if (filter.createdDate.to) {
        params.append(
          "createdDateTo",
          moment(filter.createdDate.to).format("YYYY-MM-DD")
        );
      }
      if (filter.logDate.from) {
        params.append(
          "logDateFrom",
          moment(filter.logDate.from).format("YYYY-MM-DD")
        );
      }
      if (filter.logDate.to) {
        params.append(
          "logDateTo",
          moment(filter.logDate.to).format("YYYY-MM-DD")
        );
      }

      if (filter.reportByLog) {
        params.append("reportByLog", filter.reportByLog.toString())
      }


      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/status/summary", params);
      //
      return data;
    },
    async getDetailedStatusSummaryReport({ rootState }, filter: GapStatusSummaryReportFilter) {

      // const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }

      params.append("period", filter.period);

      if (filter.campaignId) {
        params.append("campaignId", filter.campaignId.toString());
      }

      if (filter.createdDate.from) {
        params.append(
          "createdDateFrom",
          moment(filter.createdDate.from).format("YYYY-MM-DD")
        );
      }
      if (filter.createdDate.to) {
        params.append(
          "createdDateTo",
          moment(filter.createdDate.to).format("YYYY-MM-DD")
        );
      }
      if (filter.logDate.from) {
        params.append(
          "logDateFrom",
          moment(filter.logDate.from).format("YYYY-MM-DD")
        );
      }
      if (filter.logDate.to) {
        params.append(
          "logDateTo",
          moment(filter.logDate.to).format("YYYY-MM-DD")
        );
      }

      if (filter.reportByLog) {
        params.append("reportByLog", filter.reportByLog.toString())
      }


      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/status/summary/detailed", params);
      //
      return data;
    },
    async getTransportationCoordinationReport({ rootState }, fiter: GapTransportationCoordinationReportFilter) {
      const filter = fiter;
      //const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }


      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      if (filter.subStatusList && filter.subStatusList.length) {
        params.append(
          "subStatusList",
          filter.subStatusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }

      if (filter.insurerList && filter.insurerList.length) {
        params.append("insurerList", filter.insurerList.join(","));
      }

      if (filter.admissionDischargeList && filter.admissionDischargeList.length) {
        params.append(
          "admissionDischargeList",
          filter.admissionDischargeList
            .map((admissionDischarge) => {
              return "'" + admissionDischarge + "'";
            })
            .join(",")
        );
      }

      if (filter.priorityList && filter.priorityList.length) {
        params.append(
          "priorityList",
          filter.priorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join(",")
        );
      }

      if (filter.apptPriorityList && filter.apptPriorityList.length) {
        params.append(
          "ApptriorityList",
          filter.apptPriorityList
            .map((priority) => {
              return "'" + priority + "'";
            })
            .join(",")
        );
      }

      if (filter.referenceDate.from) {
        params.append(
          "referenceDateFrom",
          moment(filter.referenceDate.from).format("YYYY-MM-DD")
        );

      }

      if (filter.referenceDate.to) {

        params.append(
          "referenceDateTo",
          moment(filter.referenceDate.to).format("YYYY-MM-DD")
        );

      }

      if (filter.apptDate && filter.apptDate.from) {
        params.append(
          "apptDateFrom",
          moment(filter.apptDate.from).format("YYYY-MM-DD")
        );

      }

      if (filter.apptDate && filter.apptDate.to) {

        params.append(
          "apptDateTo",
          moment(filter.apptDate.to).format("YYYY-MM-DD")
        );

      }

      if (filter.campaignId) {
        params.append("campaignId", filter.campaignId.toString());
      }

      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/appt/transportation/Coordination", params);
      //
      return data;
    },
    async getCoordinationSummaryReport({ rootState }, fiter: GapCoordinationSummaryReportFilter) {
      const filter = fiter;
      //const { filter } = rootState.gaps as GapState;
      // SET FORM DATA
      const params = new FormData();
      const entityId: string = rootState.auth.entityId;
      if (entityId) {
        params.append("entityId", entityId);
      }

      //test
      if (filter.statusList && filter.statusList.length) {
        params.append(
          "statusList",
          filter.statusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }
      if (filter.subStatusList && filter.subStatusList.length) {
        params.append(
          "subStatusList",
          filter.subStatusList
            .map((status) => {
              return "'" + status + "'";
            })
            .join(",")
        );
      }

      if (filter.insurerList && filter.insurerList.length) {
        params.append("insurerList", filter.insurerList.join(","));
      }

      if (filter.admissionDischargeList && filter.admissionDischargeList.length) {
        params.append(
          "admissionDischargeList",
          filter.admissionDischargeList
            .map((admissionDischarge) => {
              return "'" + admissionDischarge + "'";
            })
            .join(",")
        );
      }

      params.append(
        "referenceDateFrom",
        moment(filter.referenceDate.from).format("YYYY-MM-DD")
      );
      params.append(
        "referenceDateTo",
        moment(filter.referenceDate.to).format("YYYY-MM-DD")
      );

      //end test
      if (filter.campaignId) {
        params.append("campaignId", filter.campaignId.toString());
      }

      // GET REPORT ASYNC
      const { data } = await Vue.axios.post("gaps/report/Coordination/summary", params);
      //
      return data;
    },
     async getOfficeInfo({ commit }, { publicId }) {
      // SET FORM DATA
      const params = new FormData();
      params.append("publicId",publicId);
      // SAVE REASON ASYNC
      const { data } = await Vue.axios.post("gaps/office/info", params);
      //
      return data;
    },
  },
  getters: {
    idFilterOn: (state: GapState) => {
      return filterOn(state.filter.id);
    },
    patientFilterOn: (state: GapState) => {
      return filterOn(state.filter.patient);
    },
    providerFilterOn: (state: GapState) => {
      return filterOn(state.filter.provider);
    },
    measureFilterOn: (state: GapState) => {
      return filterOn(state.filter.measure);
    },
    refdateFilterOn: (state: GapState) => {
      return filterOn(state.filter.referenceDate);
    },
    priorityFilterOn: (state: GapState) => {
      return filterOn(state.filter.priorityList);
    },
    statusFilterOn: (state: GapState) => {
      return filterOn(state.filter.statusList);
    },
    subStatusFilterOn: (state: GapState) => {
      return filterOn(state.filter.subStatusList);
    },
    reasonFilterOn: (state: GapState) => {
      return filterOn(state.filter.reasonList);
    },
    ownerUserFilterOn: (state: GapState) => {
      return filterOn(state.filter.ownerUserList);
    },
    createdFilterOn: (state: GapState) => {
      return filterOn(state.filter.created);
    },
    updatedFilterOn: (state: GapState) => {
      return filterOn(state.filter.updated);
    },

  },
};
